import { FC, useState } from 'react';
import { AddButton } from '../layouts/Buttons/AddButton/AddButton';
import { InviteUsersModal } from '../layouts/Modals/InviteUsersModal/InviteUsersModal';
import { UsersTable } from '../Tables/UsersTable/UsersTable';
import { useFetcherWithUtils } from '@/hooks/useFetcherWithUtils';
import { find, findIndex } from 'lodash';
import { InvitationFromStore, PermissionFromStore, UserRole } from '@/types/types';
import { getTableUsers } from '@/pages/WorkspaceSettings/WorkspaceSettings';
import { assert } from '@/utils/asserts';
import { FirestoreError } from 'firebase/firestore';
import { useLocation } from 'react-router-dom';
import { showToast } from '@/utils/toasts';

interface Props {
  permissions: PermissionFromStore[];
  invitations: InvitationFromStore<'workspace' | 'organization'>[];
  isAdmin: boolean;
  deletePermission: (permissionId: string, entityId: string) => Promise<null>;
  deleteInvitation: (invitationId: string, entityId: string) => Promise<null>;
  changeRole: (entityId: string, userId: string, role: UserRole) => Promise<null>;
  changeInvitationRole: (
    entityId: string,
    invitationId: string,
    role: UserRole,
  ) => Promise<null>;
  entityId: string;
}

export const EntityUserManagement: FC<Props> = ({
  invitations,
  permissions,
  isAdmin,
  deleteInvitation,
  deletePermission,
  changeRole,
  entityId,
  changeInvitationRole,
}) => {
  const users = getTableUsers(permissions, invitations);

  const location = useLocation();

  let entityType: 'workspace' | 'organization' = 'workspace';
  if (location.pathname.includes('organization')) {
    entityType = 'organization';
  }

  assert(entityId, 'workspaceId is required');

  const [fetcher, isLoading] = useFetcherWithUtils();
  const [isInviteUsersModalOpen, setIsInviteUsersModalOpen] = useState(false);

  const changeRoleInternal = async (role: UserRole, userId: string) => {
    const oldPermissionIndex = findIndex(users, (el) => el.id === userId);
    const oldPermission = users[oldPermissionIndex];

    try {
      if (oldPermission.isInvitation) {
        await changeInvitationRole(entityId, oldPermission.id, role);
      } else {
        await changeRole(entityId, userId, role);
      }
    } catch (e) {
      const error = e as FirestoreError;
      console.error(error);
      if (error.code === 'permission-denied') {
        showToast(
          'Error!',
          'You do not have permission to change the role of this user',
          'error',
        );
      } else {
        showToast('Error!', 'Please try again later', 'error');
      }
    }
  };

  const handleUserDelete = async (elId: string) => {
    const copy = [...users];
    const rowId = findIndex(copy, (el) => el.id === elId);
    const oldElement = copy.splice(rowId, 1)[0];

    try {
      if (!oldElement.isInvitation) {
        await deletePermission(oldElement.id, entityId);
      } else {
        await deleteInvitation(oldElement.hash as string, entityId);
      }
    } catch (e) {
      console.error(e);
      showToast('Error!', 'Please try again later', 'error');
    }
  };

  const onDeleteSelected = (rowSelection: object) => {
    const selectedRows = Object.keys(rowSelection).map((key) => {
      const user = find(users, { id: key });
      return {
        id: user?.id,
        role: user?.role,
        hash: user?.hash,
      };
    });
    const formData = new FormData();
    formData.append('selected', JSON.stringify(selectedRows));
    formData.append('type', 'selectedUsers');
    fetcher.submit(formData, { method: 'delete' });
  };

  const extraActions = (
    <AddButton
      style={{ marginLeft: 'auto' }}
      onClick={() => setIsInviteUsersModalOpen(true)}
    >
      Add User
    </AddButton>
  );

  return isAdmin ? (
    <>
      <UsersTable
        entityType={entityType}
        users={users}
        extraActions={extraActions}
        fetcher={fetcher}
        handleUserDelete={handleUserDelete}
        isLoading={isLoading}
        onDeleteSelected={onDeleteSelected}
        updateUserRole={changeRoleInternal}
      />
      <InviteUsersModal
        usersNum={permissions.length}
        invitationsNum={invitations.length}
        isOpen={isInviteUsersModalOpen}
        setClose={() => setIsInviteUsersModalOpen(false)}
      />
    </>
  ) : null;
};
